// ==============================
// Imports
// ==============================

// MUI Components
import AppBarSpacer from '../../components/common/AppBarSpacer';
import {
  Typography,
  IconButton,
  Box,
  Button,
  Card,
  CardActionArea,
} from '@mui/material';
import PageHeader from '../../components/common/PageHeader';
import CloseIcon from '@mui/icons-material/Close';

// Libraries
import { useState } from 'react';

// Local Components
import ReuseableModal from '../../components/ReuseableModal';
import { apiGet } from '../../utils/apiClient';
import { Response } from '../../types/TrialBookingTable.type';
import SOFTable from '../../components/Assessment/SOFTable';
import CourseStageAccordion from '../../components/CourseStageAccordion';

// Hooks
import useModal from '../../hooks/useModal';

// Assets
import StarfishIcon from '../../assets/class/sof/starfish.png';
import FlyingFishIcon from '../../assets/class/flying-fish.png';
import { Registration } from '../../types/AssessmentTable.type';
import { UserStageProgress } from '../../types/CourseStageAccordion.type';
import FFTable from '../../components/Assessment/FFTable';
import AssessmentMiniDrawer from '../../components/AssessmentMiniDrawer';

// ==============================
// Component
// ==============================
const AssessmentHome = () => {
  // States
  // Sidebar state
  const [open, setOpen] = useState(true);

  // Modal states
  const { isOpen: isOpenSOF, toggle: toggleSOF } = useModal();
  const { isOpen: isOpenFF, toggle: toggleFF } = useModal();

  const [courseStages, setCourseStages] = useState<
    {
      level: string;
      stages: UserStageProgress[];
    }[]
  >([]);
  const { isOpen: isOpenLevelModal, toggle: toggleLevelModal } = useModal();
  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);
  const [registrationRefreshKey, setRegistrationRefreshKey] = useState(0);

  const handleEntryLevelUpdated = () => {
    setRegistrationRefreshKey((currentKey) => currentKey + 1);
  };

  const fetchUserStageProgress = async (
    subSubTypeId: number,
    registrationId: number,
  ) => {
    try {
      const res = await apiGet<Response<UserStageProgress>>(
        `/course/get-user-progress/${subSubTypeId}/${registrationId}`,
      );
      const groupedStages = res.data.reduce(
        (
          acc: Record<string, UserStageProgress[]>,
          stage: UserStageProgress,
        ) => {
          if (!acc[stage.levelName]) {
            acc[stage.levelName] = [];
          }
          acc[stage.levelName].push(stage);
          return acc;
        },
        {},
      );

      const courseStagesArray = Object.entries(groupedStages).map(
        ([level, stages]) => ({
          level,
          stages: stages as UserStageProgress[],
        }),
      );
      setCourseStages(courseStagesArray);
    } catch (error) {
      console.error('Failed to fetch course stages:', error);
    }
  };

  const handleOpenLevelModal = (
    subSubTypeId: number,
    registration: Registration,
  ) => {
    fetchUserStageProgress(subSubTypeId, registration.id);
    setSelectedRegistration(registration);
    toggleLevelModal();
  };

  const handleCloseLevelModal = () => {
    setCourseStages([]);
    setSelectedRegistration(null);
    toggleLevelModal();
  };

  // ==============================
  // Render
  // ==============================
  return (
    <>
      <div className="flex h-screen">
        <AssessmentMiniDrawer open={open} setOpen={setOpen} />
        <main className="flex-1 overflow-auto app-page-bg">
          <AppBarSpacer />
          <div className="h-full px-6 py-4">
            <PageHeader
              title="Assessment Tool"
              subtitle="Select an assessment program to manage student levels and progress."
            />

            {/* Assessment programs */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(4, 1fr)',
                },
                gap: 3,
                mt: 1,
              }}
            >
              {[
                {
                  title: 'SCHOOL OF FISH',
                  description: 'Assessment for SOF course',
                  icon: StarfishIcon,
                  onClick: toggleSOF,
                },
                {
                  title: 'FLYING FISH',
                  description: 'Assessment for FF course',
                  icon: FlyingFishIcon,
                  onClick: toggleFF,
                },
              ].map((program) => (
                <Card
                  key={program.title}
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    transition:
                      'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: 8,
                      borderColor: 'transparent',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={program.onClick}
                    sx={{ height: '100%', p: 4 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 136,
                          height: 136,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img
                          src={program.icon}
                          alt={`${program.title} assessment`}
                          className="h-28 w-28 object-contain"
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color="secondary.main"
                      >
                        {program.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {program.description}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              ))}
            </Box>

            {/* Dialog 1 */}
            {/* SOF Modal */}
            <ReuseableModal isOpen={isOpenSOF} toggle={toggleSOF} width="w-3/5">
              <div className="p-4 relative">
                <Typography variant="h6" color="secondary" fontWeight="bold">
                  SCHOOL OF FISH ASSESSMENT LIST
                </Typography>

                <div className="flex flex-col my-4">
                  <SOFTable
                    handleOpenLevelModal={handleOpenLevelModal}
                    refreshKey={registrationRefreshKey}
                  />
                </div>

                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                  <Button
                    onClick={toggleSOF}
                    sx={{
                      border: '1px solid #4da7db !important',
                      backgroundColor: '#4da7db !important',
                      color: '#fff !important',
                      '&:hover': {
                        backgroundColor: '#3d91bf !important',
                      },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </div>
            </ReuseableModal>

            {/* Dialog 2 */}
            {/* FF Modal */}
            <ReuseableModal isOpen={isOpenFF} toggle={toggleFF} width="w-3/5">
              <div className="p-4 relative">
                <Typography variant="h6" color="secondary" fontWeight="bold">
                  FLYING FISH ASSESSMENT LIST
                </Typography>

                <div className="flex flex-col my-4">
                  <FFTable
                    handleOpenLevelModal={handleOpenLevelModal}
                    refreshKey={registrationRefreshKey}
                  />
                </div>

                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                  <Button
                    onClick={toggleFF}
                    sx={{
                      border: '1px solid #4da7db !important',
                      backgroundColor: '#4da7db !important',
                      color: '#fff !important',
                      '&:hover': {
                        backgroundColor: '#3d91bf !important',
                      },
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </div>
            </ReuseableModal>

            {/* Level Modal */}
            <ReuseableModal
              isOpen={isOpenLevelModal}
              toggle={handleCloseLevelModal}
              width="w-4/6 z-10"
              height="h-4/5"
            >
              <div className="p-4 relative">
                <Typography variant="h6" color="secondary" fontWeight="bold">
                  Manage User Level
                </Typography>
                <Typography
                  variant="body1"
                  color="text.accent"
                  sx={{ mt: 2, mb: 2 }}
                >
                  Promote or demote the user to a different level.
                </Typography>
                <div className="flex flex-col gap-2">
                  {/* Accordion */}
                  <CourseStageAccordion
                    data={courseStages}
                    registration={selectedRegistration}
                    fetchUserStageProgress={fetchUserStageProgress}
                    onEntryLevelUpdated={handleEntryLevelUpdated}
                  />

                  {/* Button */}
                  <div className="absolute top-3 right-3 flex justify-end gap-4 z-10">
                    <IconButton onClick={handleCloseLevelModal}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              </div>
            </ReuseableModal>
          </div>
        </main>
      </div>
    </>
  );
};

export default AssessmentHome;
